import type { ConversationMessage } from 'js/src/agent/types';
import { isAssistantMessage } from 'js/src/agent/types';
import type { AssetTranscript, LanguageEnum, VoiceoverAssetResponse } from 'js/src/client';
import { ApiService, AsyncOperationMessageTypeEnum, AsyncOperationNameEnum } from 'js/src/client';
import { ImmutableFromJS } from 'js/src/libs/consts';
import { SCREEN_WIDTH_THRESHOLD } from 'js/src/libs/video-data-consts';
import WebSocketClient from 'js/src/libs/websocket-client';

/**
 * The resolutionValue used when constructing a Luminary instance to back a
 * video draft preview in Sketch. Tuned per screen size so weaker devices
 * don't spend GPU/memory on resolution they can't display.
 */
export function getVideoDraftLuminaryResolution(): number {
    return window.screen?.availWidth <= SCREEN_WIDTH_THRESHOLD ? 300 : 500;
}

export const generateVoiceoverAsset = async ({
    text,
    voice,
    languageCode,
}: {
    text: string;
    voice: string;
    languageCode: string;
}) => {
    const response = await ApiService.textToVoiceoverAssetCreate({
        body: {
            text,
            voice_preset: voice,
            language_code: languageCode,
        },
    });
    const { async_operation_uuid } = response.data;
    const ws = WebSocketClient.getInstance();
    const result = await ws.waitForMessage<VoiceoverAssetResponse>(
        async_operation_uuid,
        AsyncOperationMessageTypeEnum.ASYNC_OPERATION_RESULT,
        AsyncOperationNameEnum.CONVERT_TEXT_TO_VOICEOVER_ASSET,
    );
    return {
        voiceoverAsset: ImmutableFromJS(result.audio_asset),
        transcript: {
            transcriptWords: result.transcript,
            language: result.language,
        } as AssetTranscript,
    };
};

export const createRecommendationAIVoiceover = async ({
    script,
    language,
    voiceId,
}: {
    script: string;
    language: LanguageEnum;
    voiceId: string;
}) => {
    if (!voiceId) {
        throw new Error('No AI voice selected for video plan');
    }
    const startTime = Date.now();
    const { voiceoverAsset, transcript } = await generateVoiceoverAsset({
        text: script,
        voice: voiceId,
        languageCode: language,
    });
    const endTime = Date.now();
    analytics.track('Voiceover Created for Video Plan', {
        duration: endTime - startTime,
        voiceoverDurationSeconds: voiceoverAsset.get('duration'),
        voiceId,
    });
    return { voiceoverAsset, transcript };
};

/**
 * Whether the agent's final, user-facing reply has begun this turn.
 *
 * The toolbox hands off from the generating preview to the editable scene list
 * once the agent stops building and starts addressing the user. The agent loop
 * alternates between tool-call messages (which build the video) and prose
 * messages (which talk to the user); the turn's final message is the prose one
 * with no tool calls. Because an assistant message carries either prose OR tool
 * calls — never both — prose as the latest message marks the build as finished
 * and the final reply as beginning.
 *
 * Detecting this before the turn ends lets the handoff start while the reply is
 * being written, overlapping the video-create round trip with the prose instead
 * of stranding the user on the generating preview after the chat says it's done.
 *
 * Accepts a still-running turn in either state: a streaming agent reports
 * 'streaming' and this fires on the first prose tokens, while a non-streaming
 * one (the toolbox) stays 'loading' and delivers the reply whole. It stays false
 * once the turn has settled to 'idle', where the turn-end fallback takes over.
 */
export function hasFinalAssistantReplyStarted(state: {
    status: string;
    messages: ConversationMessage[];
}): boolean {
    if (state.status !== 'streaming' && state.status !== 'loading') {
        return false;
    }
    const lastMessage = state.messages[state.messages.length - 1];
    if (!lastMessage || !isAssistantMessage(lastMessage)) {
        return false;
    }
    if (lastMessage.tool_calls?.length) {
        return false;
    }
    return typeof lastMessage.content === 'string' && lastMessage.content.trim().length > 0;
}
